import type { PaginatedResponse } from '../common.js';
import type { ApBillStatus } from '../generated/enums.js';
import type {
  ReconciliationInboxLinkDetail,
  ReconciliationOriginAccountSummary,
  StatementImportSummary,
  TopCandidateDetails,
  VendorAlternateBill,
} from './bank-transactions.js';
import type {
  CardItemManagerReviewStatus,
  CardStatementItemAnnotationStatus,
  CardStatementItemAnnotationType,
  CardStatementStatus,
  ReconciliationMethod,
  ReconciliationStatus,
} from './enums.js';

/** Canonical write guard for card items that have not acquired financial/reconciliation links. */
export const CARD_ITEM_FINANCIALLY_MUTABLE_WHERE = {
  reconciliationStatus: 'PENDING',
  fastTrackBillId: null,
  fastTrackStatus: null,
  classification: { is: null },
} as const;

export const CARD_ITEM_FINANCIAL_PROTECTION_WHERE = {
  OR: [
    { reconciliationStatus: { not: 'PENDING' } },
    { fastTrackBillId: { not: null } },
    { fastTrackStatus: { not: null } },
    { classification: { isNot: null } },
  ],
} as const;

export const CARD_STATEMENT_FINANCIALLY_MUTABLE_WHERE = {
  status: { in: ['OPEN', 'CLOSED'] },
  reviewedAt: null,
  items: { none: CARD_ITEM_FINANCIAL_PROTECTION_WHERE },
} as const;

export const CARD_ITEM_AND_STATEMENT_FINANCIALLY_MUTABLE_WHERE = {
  ...CARD_ITEM_FINANCIALLY_MUTABLE_WHERE,
  OR: [{ statementId: null }, { statement: { is: CARD_STATEMENT_FINANCIALLY_MUTABLE_WHERE } }],
} as const;

export interface CardStatementSummary {
  id: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  closingDate: string;
  status?: CardStatementStatus;
}

/**
 * Complete credit card statement response
 * Represents a monthly credit card bill/statement
 */
export interface CardStatementResponse {
  /** Unique identifier */
  id: string;

  /** Credit card account this statement belongs to */
  cardAccountId: string;

  /** Period start date */
  periodStart: string;

  /** Period end date */
  periodEnd: string;

  /** Due date for payment */
  dueDate: string;

  /** Statement closing date */
  closingDate: string;

  /** Technical month bucket used for provider/idempotency grouping. */
  cycleBucketDate?: string;

  /** True when closingDate was estimated because the provider omitted it. */
  closingDateEstimated?: boolean;

  /** Total amount due */
  totalAmount: number;

  /** Name of the credit card (enriched) */
  cardName?: string;

  /** Card brand (enriched) */
  brand?: string;

  /** Last 4 digits of the card (enriched) */
  last4?: string;

  /** Number of items in this statement (enriched) */
  itemCount?: number;

  /** Current status */
  status: CardStatementStatus;

  /** When the statement was reviewed by an authorized manager */
  reviewedAt?: string | null;

  /** User that reviewed the statement */
  reviewedByUserId?: string | null;

  /** Display name of the user that reviewed the statement */
  reviewedByName?: string | null;

  /** Notes captured when marking the statement as reviewed */
  reviewNotes?: string | null;

  /** Convenience flag derived from reviewedAt */
  isReviewed?: boolean;

  /** Explicit derived invoice review state. */
  isInvoiceReviewed?: boolean;

  /** Explicit card-level review state for manager dashboard projections. */
  isCardReviewed?: boolean;
  cardReviewedAt?: string | null;
  cardReviewedByUserId?: string | null;
  cardReviewedByName?: string | null;

  /** Derived review state for the complete invoice group. */
  invoiceReview?: CardInvoiceReviewSummary;

  /** Statement items (optional, can be loaded separately) */
  items?: CardStatementItemResponse[];

  /** When this statement was created */
  createdAt: string;

  /** When this statement was last updated */
  updatedAt: string;
}

/**
 * The posting-book currency of an entity, for reconciliation screens. Served under `op:read`, so
 * a reconciliation user needs no GL books permission to see amounts in the right currency.
 * `null` when the entity has no book or its books disagree.
 */
export interface ReconciliationEntityFunctionalCurrencyResponse {
  currency: string | null;
}

/**
 * Individual item from credit card statement
 * Represents a single transaction on the card
 */
export interface CardStatementItemResponse {
  /** Unique identifier */
  id: string;

  /** Parent statement ID */
  statementId: string;

  /** Transaction date */
  postedAt: string;

  /**
   * Transaction amount, signed (ALC-913): negative = purchase/charge,
   * positive = refund/estorno/credit that reduces the balance owed.
   */
  amount: number;

  /** ISO 4217 currency of `amount`, from the card account; absent when the card has none. */
  currencyCode?: string;

  /** Business meaning derived from provider evidence, description and signed amount. */
  kind?: 'PURCHASE' | 'REFUND' | 'STATEMENT_PAYMENT' | 'UNKNOWN';

  /** Stable rule used to derive kind, for audit and support. */
  kindRule?: string;

  /** Child/additional/virtual card dimension from Pluggy. */
  childCardLast4?: string;

  /** Provider lifecycle status, independent from reconciliation status. */
  providerStatus?: string;

  /** Original purchase timestamp when the provider distinguishes it from posting. */
  originalPurchaseAt?: string;

  /** Merchant name */
  merchantName: string;

  /** Merchant Category Code */
  mcc?: string;

  /** Raw description from statement */
  descriptionRaw: string;

  /** Deterministic operational category for dashboard rollups */
  category?: string;

  /** Installment number (if applicable) */
  installmentNumber?: number;

  /** Total installments (if applicable) */
  installmentTotal?: number;

  /** IOF amount in display units (e.g. 6.06 for R$ 6,06) – shown when present */
  iofAmount?: number;

  /** Original amount in foreign currency display units (e.g. 31.50 for USD 31,50) */
  foreignCurrencyAmount?: number;

  /** Foreign currency code (USD, EUR, etc.) – when transaction was in foreign currency */
  foreignCurrencyCode?: string;

  /** Current reconciliation status */
  reconciliationStatus: ReconciliationStatus;

  /** Manager review status, independent from AP reconciliation/classification */
  managerReviewStatus?: CardItemManagerReviewStatus;

  /** Reason captured when the manager marks the transaction as improper */
  managerReviewReason?: string | null;

  /** When the manager reviewed this item */
  managerReviewedAt?: string | null;

  /** User that reviewed this item */
  managerReviewedByUserId?: string | null;

  /** Confidence score for AI matching (0-1) */
  confidence?: number;

  /** Method used for reconciliation */
  matchMethod?: ReconciliationMethod;

  /** Name of the card account (enriched field) */
  accountName?: string;

  /** Compact card account identity for inbox UI */
  originAccount?: ReconciliationOriginAccountSummary;

  /** Card account ID (enriched from statement – used for filtering and grouping) */
  accountId?: string;

  /** Reconciliation group ID if status is RECONCILED */
  reconciliationGroupId?: string;

  /** Number of reconciliation links in the group (> 1 means N:1 split) */
  reconciliationLinkCount?: number;

  /** Total allocated amount across all links in the group */
  reconciliationLinkedTotal?: number;

  /** Individual amounts for each link (for "R$ 53,94 + R$ 37,58 + …" display) */
  reconciliationLinkAmounts?: number[];

  /** Full per-link detail, one entry per reconciliation link, for a stacked row per link. */
  reconciliationLinks?: ReconciliationInboxLinkDetail[];

  /** GL posting status for reconciled items */
  glStatus?: 'POSTED' | 'SKIPPED' | 'FAILED';

  /** Reason for GL posting status */
  glReason?: string;

  /** Statement import reference (flattened from the parent statement, when available) */
  statementImportId?: string;
  statementImport?: StatementImportSummary;

  /** Parent statement summary (for list grouping and headers) */
  statement?: CardStatementSummary;

  /** When this item was created */
  createdAt: string;

  /** When this item was last updated */
  updatedAt: string;

  /** Number of match candidates (populated from MatchCandidate table) */
  candidateCount?: number;

  /** Linked account name when reconciled (e.g. transfer destination) */
  linkedAccountName?: string;

  /** Min/max confidence range when multiple candidates exist */
  scoreRange?: { min: number; max: number };

  /** Name of the top candidate (suggested account) */
  topCandidateName?: string;

  /** Type of the top candidate (AP_BILL, AP_PAYMENT, AR_INVOICE, BANK_TX, etc.) – used to show account type badge */
  topCandidateType?: string;

  /** Matched entity id for the top candidate (billId, paymentId, etc.) – for apply flows aligned with inbox display */
  topCandidateMatchedId?: string;

  /** For AP_PAYMENT: parent bill ID needed to build the detail URL */
  topCandidateBillId?: string;

  /** When top candidate is AP_BILL / AP_PAYMENT: true if the bill is a reimbursement */
  topCandidateIsReimbursement?: boolean;

  /** Amount of the top candidate (suggested conta) – always use this for right card when present */
  topCandidateAmount?: number;

  /** Currency of the top candidate (USD, EUR, BRL) – for multi-currency display */
  topCandidateCurrency?: string;

  /** Date of the top candidate (due/scheduled date from conta) – use for right card when present */
  topCandidateDate?: string;

  /** Type-specific details for the top candidate (AP/AR/transfer) – for richer card display */
  topCandidateDetails?: TopCandidateDetails;

  /** See BankTransactionResponse.topCandidateReasonCode. Always unset for card items today. */
  topCandidateReasonCode?: 'TAX_ON_GROSS';

  /** Other unreconciled bills from the same vendor (populated on MATCHED items only) */
  vendorAlternateBills?: VendorAlternateBill[];

  /** When reconciled with adjustment: target/conta amount (e.g. R$ 1.499) */
  adjustmentTargetAmount?: number;
  /** When reconciled with adjustment: total adjustment amount (e.g. R$ 1.329) */
  adjustmentAmount?: number;
  /** Primary adjustment type for display (JUROS, DESCONTO, MULTA, IRRF, etc.) */
  adjustmentType?: string;

  /** Details for the confidence score (explanations) */
  confidenceDetails?: {
    factor: string;
    weight: number;
    matched: boolean;
    reason: string;
    metadata?: Record<string, unknown>;
  }[];

  /** Manager observations and finance questions attached to this transaction */
  annotations?: CardStatementItemAnnotationResponse[];
}

export interface CardInvoiceSubtotalResponse {
  statementId: string;
  cardAccountId: string;
  cardName: string;
  last4?: string;
  informedTotal: number | null;
  calculatedTotal: number;
  itemCount: number;
}

export const CARD_STATEMENT_TOTAL_SOURCE = {
  DERIVED_ITEMS: 'DERIVED_ITEMS',
  DOCUMENT_REPORTED: 'DOCUMENT_REPORTED',
  PROVIDER_REPORTED: 'PROVIDER_REPORTED',
} as const;
export type CardStatementTotalSource =
  (typeof CARD_STATEMENT_TOTAL_SOURCE)[keyof typeof CARD_STATEMENT_TOTAL_SOURCE];

export interface CardInvoiceDetailResponse {
  groupKey: string;
  isComplete: boolean;
  informedTotal: number | null;
  calculatedTotal: number;
  divergence: number | null;
  purchaseTotal: number;
  refundTotal: number;
  visibleItemCount: number;
  statementPaymentCount: number;
  unknownCount: number;
  paidAmount: number;
  providerPaidAmount?: number;
  apPaidAmount?: number;
  outstandingAmount: number;
  settlementStatus: 'UNLINKED' | 'CLOSED' | 'PARTIAL' | 'PAID';
  paymentEvidence?: {
    id: string;
    valueType: 'INSTALLMENT_PAYMENT' | 'FULL_PAYMENT' | 'OTHER_PAYMENT';
    paymentDate: string;
    paymentMode?: 'DEBIT_ACCOUNT' | 'BANK_SLIP' | 'PAYROLL_DEDUCTION' | 'PIX';
    amount: number;
    currencyCode: string;
  }[];
  subtotals: CardInvoiceSubtotalResponse[];
  childCardSubtotals?: {
    last4: string;
    calculatedTotal: number;
    itemCount: number;
  }[];
  audit?: {
    affectedItems: number;
    pendingOld: number;
    postedWithoutStatement: number;
    unknownChildCard: number;
    creditsWithoutType: number;
    invalidMatching: number;
    glFailure: number;
  };
  items: CardStatementItemResponse[];
}

/**
 * Paginated list of card statements
 */
export type CardStatementListResponse = PaginatedResponse<CardStatementResponse>;

/**
 * Paginated list of card statement items
 */
export type CardStatementItemListResponse = PaginatedResponse<CardStatementItemResponse>;

/**
 * Query parameters for finding card statements
 */
export interface CardStatementQuery {
  /** Filter by card account */
  cardAccountId?: string;

  /** Filter by status */
  status?: CardStatementStatus;

  /** Filter by period start date */
  periodStart?: string;

  /** Filter by period end date */
  periodEnd?: string;
}

/**
 * Query parameters for finding card statement items
 */
export interface CardStatementItemQuery {
  /** Filter by statement */
  statementId?: string;

  /** Filter by card account */
  cardAccountId?: string;

  /** Filter by reconciliation status */
  reconciliationStatus?: ReconciliationStatus;

  /** Filter by minimum amount */
  minAmount?: number;

  /** Filter by maximum amount */
  maxAmount?: number;

  /** Search in merchant and description */
  search?: string;
}

export interface ReviewCardStatementRequest {
  /** Optional review notes stored with the confirmation */
  notes?: string | null;
}

export interface UpdateCardReviewRequest {
  /** Credit card being reviewed. */
  creditCardId: string;
  /** Statement/competency selected by the user. */
  statementId: string;
  /** True marks the card reviewed; false reverses the review. */
  reviewed: boolean;
  /** Optional notes stored with the card review. */
  notes?: string | null;
}

export const CARD_STATEMENT_REVIEW_STATE_SOURCE = {
  LEGACY: 'LEGACY',
  DERIVED: 'DERIVED',
} as const;

export type CardStatementReviewStateSource =
  (typeof CARD_STATEMENT_REVIEW_STATE_SOURCE)[keyof typeof CARD_STATEMENT_REVIEW_STATE_SOURCE];

export interface CardReviewState {
  isReviewed: boolean;
  /** False for synthetic invoice hosts or cards without identifiable transactions. */
  isReviewable?: boolean;
  reviewedAt: string | null;
  reviewedByUserId: string | null;
  reviewedByName: string | null;
  reviewNotes: string | null;
}

export interface CardInvoiceReviewSummary {
  groupKey: string;
  isReviewed: boolean;
  reviewedAt: string | null;
  reviewedByUserId: string | null;
  reviewedByName: string | null;
  reviewedCardCount: number;
  totalCardCount: number;
  unresolvedTransactionCount: number;
  legacyReviewedAt: string | null;
}

export interface UpdateCardReviewResponse {
  creditCardId: string;
  statementId: string;
  cardReview: CardReviewState;
  invoiceReview: CardInvoiceReviewSummary;
}

export interface CardStatementItemAnnotationResponse {
  /** Unique annotation identifier */
  annotationId: string;

  /** Card statement item the annotation belongs to */
  itemId: string;

  /** Annotation kind */
  annotationType: CardStatementItemAnnotationType;

  /** Annotation workflow status */
  status: CardStatementItemAnnotationStatus;

  /** Annotation text */
  content: string;

  /** Author user identifier */
  authorUserId: string;

  /** Author display name */
  authorName?: string | null;

  /** Creation timestamp */
  createdAt: string;

  /** Last update timestamp */
  updatedAt: string;
}

export interface CreateCardStatementItemAnnotationRequest {
  /** Annotation kind */
  annotationType: CardStatementItemAnnotationType;

  /** Annotation text */
  content: string;
}

export interface UpdateCardItemManagerReviewRequest {
  /** Structured manager review status for the card item */
  status: Exclude<CardItemManagerReviewStatus, 'PENDING'>;

  /** Optional reason, required by the UI when marking an item as improper */
  reason?: string | null;
}

export interface CreditCardManagerDashboardQuery {
  /** Optional explicit statement to load */
  statementId?: string;

  /** Filter statements whose closing date is on/after this date */
  periodStart?: string;

  /** Filter statements whose closing date is on/before this date */
  periodEnd?: string;

  /** Transaction page */
  page?: number;

  /** Transaction page size */
  limit?: number;
}

export interface CreditCardManagerDashboardStatement {
  id: string;
  cardAccountId: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  closingDate: string;
  totalAmount: number;
  status: CardStatementStatus;
  transactionCount: number;
  vendorCount: number;
  reviewedAt: string | null;
  reviewedByUserId: string | null;
  reviewedByName: string | null;
  reviewNotes: string | null;
  /** Backwards-compatible alias for the derived invoice review state. */
  isReviewed: boolean;
  isInvoiceReviewed: boolean;
  isCardReviewed: boolean;
  cardReviewedAt: string | null;
  cardReviewedByUserId: string | null;
  cardReviewedByName: string | null;
  cardReview: CardReviewState;
  invoiceReview: CardInvoiceReviewSummary;
}

export interface CreditCardManagerDashboardTransaction {
  id: string;
  statementId: string;
  date: string;
  merchant: string;
  description: string;
  category: string;
  installment: string;
  status: 'classified' | 'uncategorized' | 'infoRequested';
  reconciliationStatus: ReconciliationStatus;
  managerReviewStatus?: CardItemManagerReviewStatus;
  managerReviewReason?: string | null;
  managerReviewedAt?: string | null;
  managerReviewedByUserId?: string | null;
  amount: number;
  mcc: string | null;
  aiSuggestion: string;
  aiConfidence: number;
  iofAmount?: number;
  foreignCurrencyAmount?: number;
  foreignCurrencyCode?: string;
  linkedBill?: {
    billId: string;
    vendorName: string | null;
    status: ApBillStatus;
    totalAmount: number | null;
  } | null;
  apClassification?: {
    glAccounts: string[];
    managerialAccounts: string[];
    costCenters: string[];
    departments: string[];
    customFields: { label: string; value: string }[];
  } | null;
  annotations: CardStatementItemAnnotationResponse[];
}

export interface CreditCardManagerDashboardInsightTile {
  id: string;
  label: string;
  value: string;
  detail: string;
  tone: 'neutral' | 'warning' | 'danger';
}

export interface CreditCardManagerDashboardInsightListItem {
  id: string;
  label: string;
  description: string;
  amount: number;
}

export interface CreditCardManagerDashboardCategoryComparison {
  category: string;
  currentAmount: number;
  priorAmount: number;
}

export interface CreditCardManagerDashboardResponse {
  creditCardId: string;
  card: {
    id: string;
    brand: string;
    holderName: string;
    description: string;
    lastFour: string;
    status: string;
    creditLimit?: number | null;
    availableLimit?: number | null;
  };
  currentStatement: CreditCardManagerDashboardStatement | null;
  previousStatement: CreditCardManagerDashboardStatement | null;
  insightTiles: CreditCardManagerDashboardInsightTile[];
  newVendors: CreditCardManagerDashboardInsightListItem[];
  increasedVendors: CreditCardManagerDashboardInsightListItem[];
  comparisons: CreditCardManagerDashboardCategoryComparison[];
  transactions: {
    data: CreditCardManagerDashboardTransaction[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  };
  narrative: {
    amountLabel: string;
    deltaLabel: string;
    newVendorsLabel: string;
    pendingLabel: string;
  };
}

export interface CreditCardManagerDashboardSummariesQuery {
  /** Filter statements whose closing date is on/after this date */
  periodStart?: string;

  /** Filter statements whose closing date is on/before this date */
  periodEnd?: string;

  /** Optional subset of credit cards (defaults to all tenant cards) */
  creditCardIds?: string[];
}

export interface CreditCardManagerDashboardPeriodStatement {
  statementId: string;
  totalAmount: number;
  status: CardStatementStatus;
  closingDate: string;
  dueDate: string;
  isReviewed: boolean;
  isInvoiceReviewed: boolean;
  isCardReviewed: boolean;
  cardReviewedAt: string | null;
  cardReviewedByUserId: string | null;
  cardReviewedByName: string | null;
  invoiceReview: CardInvoiceReviewSummary;
  transactionCount: number;
}

export interface CreditCardManagerDashboardCardSummary {
  creditCardId: string;
  creditLimit: number | null;
  availableLimit: number | null;
  periodStatement: CreditCardManagerDashboardPeriodStatement | null;
  invoiceBadge: string | null;
  utilizationPercent: number | null;
}

export interface CreditCardManagerDashboardSummariesResponse {
  summaries: CreditCardManagerDashboardCardSummary[];
  /** Number of cards with reviewable transactions awaiting manager review. */
  cardsNeedingReviewCount: number;
  /** Number of cards with reviewable statements due within the next seven days. */
  cardsDueSoonCount: number;
  /** @deprecated Use cardsNeedingReviewCount. Kept for older clients. */
  statementsNeedingReviewCount: number;
  /** @deprecated Use cardsDueSoonCount. Kept for older clients. */
  statementsDueSoonCount: number;
}

/**
 * Request payload for importing credit card statement
 */
export interface ImportCardStatementRequest {
  /** Card account to import into */
  cardAccountId: string;

  /** Period start date */
  periodStart: string;

  /** Period end date */
  periodEnd: string;

  /** Due date */
  dueDate: string;

  /** Closing date */
  closingDate: string;

  /** Total amount */
  totalAmount: number;

  /** Statement items */
  items: CardStatementItemImport[];
}

/**
 * Individual item for card statement import
 */
export interface CardStatementItemImport {
  /** Transaction date */
  postedAt: string;

  /** Amount */
  amount: number;

  /** Merchant name */
  merchantName: string;

  /** Merchant Category Code */
  mcc?: string;

  /** Raw description */
  descriptionRaw: string;

  /** Installment info (if applicable) */
  installmentInfo?: CardInstallmentInfo;
}

/**
 * Information about installment payments
 */
export interface CardInstallmentInfo {
  /** Current installment number */
  installmentNumber: number;

  /** Total number of installments */
  installmentTotal: number;

  /** Total amount of the purchase */
  totalAmount: number;

  /** Remaining amount to be paid */
  remainingAmount: number;
}

/**
 * Response from card statement import operation
 */
export interface ImportCardStatementResponse {
  /** Statement that was created/updated */
  statement: CardStatementResponse;

  /** Number of items imported */
  itemsImported: number;

  /** Number of duplicates found */
  duplicates: number;

  /** Number of failed items */
  failed: number;

  /** Import errors */
  errors: CardImportError[];
}

/**
 * Error details for failed card statement imports
 */
export interface CardImportError {
  /** Error message */
  message: string;

  /** Item that failed */
  item?: CardStatementItemImport;

  /** Row number if applicable */
  row?: number;
}

/**
 * How Conciliação Rápida posts a card row without asking for a vendor.
 *
 * The API stamps one of these on a listed row, and the fast-track screen reads it to lock the
 * vendor cell. One union, one predicate, one place: before ALC-4254 the API held the values as
 * local literals while `apps/web` re-declared the union by hand and three components each carried
 * their own copy of the test — so adding a market meant editing four files and hoping.
 */
export const AUTOMATIC_CARD_FEE_HANDLINGS = [
  /** Clara Brazil's standalone IOF on an international purchase. */
  'CLARA_IOF_BANK_FEE',
  /** Conta Simples' standalone IOF charge. */
  'CONTA_SIMPLES_IOF_BANK_FEE',
  /** A Clara Mexico card charge: commission, moratorium interest, late charge, or the IVA on them. */
  'CLARA_MX_CARD_FEE',
] as const;

export type AutomaticCardFeeHandling = (typeof AUTOMATIC_CARD_FEE_HANDLINGS)[number];

/**
 * True when the row posts automatically, so the screen must NOT prompt for a vendor: the posting
 * rule supplies the synthetic provider vendor.
 *
 * Takes the whole row rather than the bare value so every call site reads the same way, and
 * accepts a loose shape because the web DTO and the API projection are different types.
 */
export function isAutomaticCardFeeHandling(item: { automaticHandling?: string | null }): boolean {
  return (
    item.automaticHandling != null &&
    (AUTOMATIC_CARD_FEE_HANDLINGS as readonly string[]).includes(item.automaticHandling)
  );
}
